// Launch reveal: while the site is hidden the whole viewport is covered with a
// canvas of black paper sheets taped shut, with a few hairline seams that let tiny
// slivers of the real site peek through. The cover polls the curtain endpoint; the
// moment the site is flipped to visible the sheets drop away one by one.

import { fetchJson } from './util/fetch-json';

type CurtainResponse = {
    hidden: boolean;
};

type Sheet = {
    sprite: HTMLCanvasElement;
    spriteW: number;
    spriteH: number;
    homeX: number;
    homeY: number;
    baseAngle: number;
    order: number;
    releaseAt: number;
    released: boolean;
    gone: boolean;
    x: number;
    y: number;
    vx: number;
    vy: number;
    angle: number;
    vAngle: number;
};

const POLL_INTERVAL = 5_000;
const FETCH_TIMEOUT = 8_000;

const CELL_TARGET = 360;
const OVERLAP = 6;
const PAD = 9;

const STAGGER = 150;
const STAGGER_JITTER = 45;
const GRAVITY = 2_400;
const FADE_MS = 500;

const rand = (min: number, max: number): number => min + Math.random() * (max - min);

const cssVar = (name: string, fallback: string): string => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();

    return value !== '' ? value : fallback;
};

// Walks the four sides of a rectangle as a jagged "torn paper" path. The edge mostly
// bulges slightly outward (so neighbouring sheets overlap and cover the seam), with
// the occasional inward notch that leaves a hairline gap.
const tracePaper = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void => {
    const amp = 3.4;
    const step = 17;

    const walk = (fromX: number, fromY: number, toX: number, toY: number, outX: number, outY: number, first: boolean): void => {
        const dx = toX - fromX;
        const dy = toY - fromY;
        const length = Math.hypot(dx, dy);
        const steps = Math.max(2, Math.round(length / step));

        for (let index = first ? 0 : 1; index <= steps; index++) {
            const t = index / steps;
            const inward = Math.random() < 0.14;
            const offset = inward ? -rand(4, 10) : rand(-amp, amp * 0.4);
            const px = fromX + dx * t + outX * offset;
            const py = fromY + dy * t + outY * offset;

            if (first && index === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
    };

    ctx.beginPath();
    walk(x, y, x + w, y, 0, -1, true);
    walk(x + w, y, x + w, y + h, 1, 0, false);
    walk(x + w, y + h, x, y + h, 0, 1, false);
    walk(x, y + h, x, y, -1, 0, false);
    ctx.closePath();
};

const drawTape = (ctx: CanvasRenderingContext2D, cx: number, cy: number, length: number, thickness: number, angle: number): void => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.globalAlpha = 0.94;

    const half = thickness / 2;
    const gradient = ctx.createLinearGradient(0, -half, 0, half);
    gradient.addColorStop(0, '#aab0b4');
    gradient.addColorStop(0.45, '#d9dcde');
    gradient.addColorStop(0.55, '#e7e9ea');
    gradient.addColorStop(1, '#9aa0a4');

    ctx.fillStyle = gradient;
    ctx.fillRect(-length / 2, -half, length, thickness);

    // Frayed long edges plus a couple of scuff lines to read as matte duct tape.
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = '#74797e';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-length / 2, -half + 0.5);
    ctx.lineTo(length / 2, -half + 0.5);
    ctx.moveTo(-length / 2, half - 0.5);
    ctx.lineTo(length / 2, half - 0.5);
    ctx.stroke();

    ctx.globalAlpha = 0.16;
    ctx.beginPath();

    for (let line = 0; line < 3; line++) {
        const ly = rand(-half + 3, half - 3);
        ctx.moveTo(-length / 2, ly);
        ctx.lineTo(length / 2, ly);
    }

    ctx.stroke();
    ctx.restore();
};

export function initCurtain(): void {
    const host = document.querySelector<HTMLElement>('[data-swl-curtain]');

    if (host === null) {
        return;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const endpoint = host.dataset.endpoint ?? null;
    const paper = cssVar('--color-black', '#13110f');

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (context === null) {
        return;
    }

    const ctx = context;
    host.append(canvas);

    let dpr = 1;
    let viewportW = 0;
    let viewportH = 0;
    let cols = 0;
    let rows = 0;
    let cellW = 0;
    let cellH = 0;
    let sheets: Sheet[] = [];

    let revealing = false;
    let rafId = 0;
    let revealStart = 0;
    let lastFrame = 0;

    document.documentElement.classList.add('swl-curtain-locked');

    // The cover hides the site visually and blocks scroll, but the page behind it
    // stays focusable and readable to screen readers. Take every sibling of the
    // curtain out of the tab order and a11y tree until the reveal finishes.
    const siteNodes = Array.from(document.body.children).filter(
        (node): node is HTMLElement => node instanceof HTMLElement && node !== host
    );
    siteNodes.forEach(node => node.toggleAttribute('inert', true));

    // Renders one paper sheet (black paper + grain + torn edge + silver tape) into a
    // standalone sprite canvas, so it can later be transformed and dropped as a unit.
    const buildSprite = (): { sprite: HTMLCanvasElement; spriteW: number; spriteH: number } => {
        const paperW = cellW + OVERLAP * 2;
        const paperH = cellH + OVERLAP * 2;
        const spriteW = paperW + PAD * 2;
        const spriteH = paperH + PAD * 2;

        const sprite = document.createElement('canvas');
        sprite.width = Math.round(spriteW * dpr);
        sprite.height = Math.round(spriteH * dpr);

        const sctx = sprite.getContext('2d');

        if (sctx === null) {
            return { sprite, spriteW, spriteH };
        }

        // Render in CSS pixels; the dpr scale keeps the torn edges and tape crisp.
        sctx.scale(dpr, dpr);

        // Paper body, clipped to the torn silhouette so grain and shading stay inside.
        sctx.save();
        tracePaper(sctx, PAD, PAD, paperW, paperH);
        sctx.clip();

        sctx.fillStyle = paper;
        sctx.fillRect(0, 0, spriteW, spriteH);

        const grains = Math.round((paperW * paperH) / 220);

        for (let index = 0; index < grains; index++) {
            sctx.globalAlpha = rand(0.02, 0.07);
            sctx.fillStyle = Math.random() < 0.5 ? '#000000' : '#2c2825';
            sctx.fillRect(PAD + Math.random() * paperW, PAD + Math.random() * paperH, 1, 1);
        }

        sctx.globalAlpha = 0.35;
        const shade = sctx.createLinearGradient(PAD, PAD, PAD + paperW, PAD + paperH);
        shade.addColorStop(0, 'rgba(255, 255, 255, 0.02)');
        shade.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
        sctx.fillStyle = shade;
        sctx.fillRect(0, 0, spriteW, spriteH);

        sctx.restore();

        // Tape it up messily: a few short strips slapped over random edges and
        // corners, each skewed so it never looks machine-straight.
        const anchors = [
            { x: PAD + rand(0.2, 0.8) * paperW, y: PAD, angle: 0 },
            { x: PAD + rand(0.2, 0.8) * paperW, y: PAD + paperH, angle: 0 },
            { x: PAD, y: PAD + rand(0.2, 0.8) * paperH, angle: Math.PI / 2 },
            { x: PAD + paperW, y: PAD + rand(0.2, 0.8) * paperH, angle: Math.PI / 2 },
            { x: PAD, y: PAD, angle: Math.PI / 4 },
            { x: PAD + paperW, y: PAD, angle: -Math.PI / 4 },
            { x: PAD, y: PAD + paperH, angle: -Math.PI / 4 },
            { x: PAD + paperW, y: PAD + paperH, angle: Math.PI / 4 }
        ];

        const pieces = 2 + Math.floor(Math.random() * 3);

        anchors
            .sort(() => Math.random() - 0.5)
            .slice(0, pieces)
            .forEach(anchor => {
                drawTape(
                    sctx,
                    anchor.x + rand(-10, 10),
                    anchor.y + rand(-10, 10),
                    rand(38, 78),
                    rand(22, 30),
                    anchor.angle + rand(-0.5, 0.5)
                );
            });

        return { sprite, spriteW, spriteH };
    };

    const buildSheets = (): void => {
        cols = Math.max(2, Math.ceil(viewportW / CELL_TARGET));
        rows = Math.max(2, Math.ceil(viewportH / CELL_TARGET));
        cellW = viewportW / cols;
        cellH = viewportH / rows;

        sheets = [];

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const built = buildSprite();

                sheets.push({
                    sprite: built.sprite,
                    spriteW: built.spriteW,
                    spriteH: built.spriteH,
                    homeX: col * cellW + cellW / 2,
                    homeY: row * cellH + cellH / 2,
                    baseAngle: rand(-0.06, 0.06),
                    order: 0,
                    releaseAt: 0,
                    released: false,
                    gone: false,
                    x: 0,
                    y: 0,
                    vx: 0,
                    vy: 0,
                    angle: 0,
                    vAngle: 0
                });
            }
        }

        // Reveal order: mostly top to bottom (with a little jitter within a row), but
        // every so often a random sheet jumps the queue so it isn't a strict marching
        // line — every now and then one just drops out of turn.
        const pending = [...sheets]
            .map(sheet => ({ sheet, key: sheet.homeY + rand(-cellH * 0.3, cellH * 0.3) }))
            .sort((a, b) => a.key - b.key)
            .map(entry => entry.sheet);

        let order = 0;

        while (pending.length > 0) {
            const index = Math.random() < 0.22 ? Math.floor(Math.random() * pending.length) : 0;
            const [picked] = pending.splice(index, 1);
            picked.order = order++;
        }
    };

    const drawSheetAt = (sheet: Sheet, x: number, y: number, angle: number): void => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.drawImage(sheet.sprite, -sheet.spriteW / 2, -sheet.spriteH / 2, sheet.spriteW, sheet.spriteH);
        ctx.restore();
    };

    // A handful of thin slivers cleared along interior seams, so a little of the real
    // site shows through. Kept deliberately sparse ("echt niet teveel").
    const punchPeeks = (): void => {
        const count = Math.max(2, Math.min(6, Math.round((viewportW * viewportH) / 520_000)));

        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';

        for (let index = 0; index < count; index++) {
            const vertical = Math.random() < 0.5;
            const sliverLength = rand(16, 38);
            const sliverThickness = rand(2, 3.5);

            const cx = vertical
                ? Math.round(rand(1, cols - 1)) * cellW + rand(-2, 2)
                : rand(cellW * 0.3, viewportW - cellW * 0.3);
            const cy = vertical
                ? rand(cellH * 0.3, viewportH - cellH * 0.3)
                : Math.round(rand(1, rows - 1)) * cellH + rand(-2, 2);

            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(vertical ? Math.PI / 2 : 0);
            ctx.rotate(rand(-0.15, 0.15));
            ctx.fillRect(-sliverLength / 2, -sliverThickness / 2, sliverLength, sliverThickness);
            ctx.restore();
        }

        ctx.restore();
    };

    const drawStatic = (): void => {
        ctx.clearRect(0, 0, viewportW, viewportH);

        for (const sheet of sheets) {
            drawSheetAt(sheet, sheet.homeX, sheet.homeY, sheet.baseAngle);
        }

        punchPeeks();
    };

    const resize = (): void => {
        dpr = Math.min(2, window.devicePixelRatio || 1);
        viewportW = window.innerWidth;
        viewportH = window.innerHeight;

        canvas.width = Math.round(viewportW * dpr);
        canvas.height = Math.round(viewportH * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        buildSheets();
        drawStatic();
        host.classList.add('is-ready');
    };

    let pollTimer: number | undefined;

    const stopPolling = (): void => {
        window.clearInterval(pollTimer);
        pollTimer = undefined;
    };

    const finalize = (fade: boolean): void => {
        if (rafId !== 0) {
            cancelAnimationFrame(rafId);
            rafId = 0;
        }

        document.documentElement.classList.remove('swl-curtain-locked');
        siteNodes.forEach(node => node.toggleAttribute('inert', false));

        if (fade) {
            host.classList.add('is-done');
            window.setTimeout(() => host.remove(), FADE_MS + 50);
        } else {
            host.remove();
        }
    };

    const frame = (now: number): void => {
        if (revealStart === 0) {
            revealStart = now;
            lastFrame = now;
        }

        const elapsed = now - revealStart;
        const dt = Math.min(0.05, (now - lastFrame) / 1000);
        lastFrame = now;

        ctx.clearRect(0, 0, viewportW, viewportH);

        let remaining = 0;
        const falling: Sheet[] = [];

        for (const sheet of sheets) {
            if (sheet.gone) {
                continue;
            }

            if (!sheet.released && elapsed >= sheet.releaseAt) {
                sheet.released = true;
                sheet.x = sheet.homeX;
                sheet.y = sheet.homeY;
                sheet.angle = sheet.baseAngle;
                sheet.vx = rand(-70, 70);
                sheet.vy = rand(10, 70);
                sheet.vAngle = rand(-1.4, 1.4);
            }

            if (sheet.released) {
                sheet.vy += GRAVITY * dt;
                sheet.x += sheet.vx * dt;
                sheet.y += sheet.vy * dt;
                sheet.angle += sheet.vAngle * dt;

                const reach = (sheet.spriteW + sheet.spriteH) / 2;

                if (sheet.y - reach > viewportH) {
                    sheet.gone = true;

                    continue;
                }

                // Collected for a second pass so falling sheets pass in front of the
                // ones still taped up, instead of sliding behind them.
                falling.push(sheet);
            } else {
                drawSheetAt(sheet, sheet.homeX, sheet.homeY, sheet.baseAngle);
            }

            remaining++;
        }

        for (const sheet of falling) {
            drawSheetAt(sheet, sheet.x, sheet.y, sheet.angle);
        }

        if (remaining === 0) {
            finalize(false);

            return;
        }

        rafId = requestAnimationFrame(frame);
    };

    const startReveal = (): void => {
        if (revealing) {
            return;
        }

        revealing = true;
        stopPolling();

        if (reduced) {
            finalize(true);

            return;
        }

        sheets.forEach(sheet => {
            sheet.releaseAt = sheet.order * STAGGER + rand(-STAGGER_JITTER, STAGGER_JITTER);
        });

        revealStart = 0;
        rafId = requestAnimationFrame(frame);
    };

    const poll = async (): Promise<void> => {
        if (endpoint === null || revealing) {
            return;
        }

        // Returns null on a network hiccup or timeout, which just keeps the cover in
        // place until the next poll.
        const payload = await fetchJson<CurtainResponse>(endpoint, FETCH_TIMEOUT);

        if (payload && payload.hidden === false) {
            startReveal();
        }
    };

    const startPolling = (): void => {
        if (pollTimer !== undefined || endpoint === null) {
            return;
        }

        pollTimer = window.setInterval(() => void poll(), POLL_INTERVAL);
    };

    let resizeTimer: number | undefined;

    window.addEventListener('resize', () => {
        if (revealing) {
            return;
        }

        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(resize, 200);
    });

    document.addEventListener('visibilitychange', () => {
        if (revealing) {
            return;
        }

        if (document.hidden) {
            stopPolling();

            return;
        }

        void poll();
        startPolling();
    });

    resize();

    if (!document.hidden) {
        // Poll once right away so an already-visible site isn't needlessly covered for
        // a full interval before the first scheduled poll runs.
        void poll();
        startPolling();
    }
}
